import React, { useEffect, useState } from 'react';
import { ChevronLeftIcon, MagnifyingGlassIcon, EyeIcon } from '@heroicons/react/24/outline';
import { apiService, SubscriptionOrder, FindAllSubscriptionOrdersParams } from '../services/api';

interface SubscriptionOrderManagementProps {
  onBack?: () => void;
}

const SubscriptionOrderManagement: React.FC<SubscriptionOrderManagementProps> = ({ onBack }) => {
  const [orders, setOrders] = useState<SubscriptionOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params: FindAllSubscriptionOrdersParams = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? (statusFilter as any) : undefined,
      };

      const response = await apiService.getSubscriptionOrders(params);
      // Backend returns richer `orders` with relations (user, product, promo, affiliate)
      setOrders(response.orders || []);
      setTotal(response.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch subscription orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchTerm, statusFilter]);

  const formatPrice = (amount: number) => {
    const num = typeof amount === 'number' ? amount : parseFloat(String(amount || 0));
    if (isNaN(num) || !isFinite(num)) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      case 'REFUNDED':
        return 'bg-blue-100 text-blue-800';
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'settlement':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
      case 'deny':
      case 'cancel':
        return 'bg-red-100 text-red-800';
      case 'expired':
      case 'expire':
        return 'bg-gray-100 text-gray-800';
      case 'refunded':
        return 'bg-blue-100 text-blue-800';
      case 'initiated':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const openDetail = (order: any) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const closeDetail = () => {
    setShowDetailModal(false);
    setSelectedOrder(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-900">Subscription Orders</h1>
        </div>
        <div className="text-sm text-gray-500">
          Total Orders: {total}
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center flex-wrap gap-4">
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by order number, user, or product..."
                value={searchTerm}
                onChange={(e) => {
                  setCurrentPage(1);
                  setSearchTerm(e.target.value);
                }}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-imola-primary focus:border-imola-primary sm:text-sm"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setCurrentPage(1);
                setStatusFilter(e.target.value);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value="all">All</option>
              <option value="PENDING">Pending</option>
              <option value="PAID">Paid</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Page Size:</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setCurrentPage(1);
                setPageSize(Number(e.target.value));
              }}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    No subscription orders found
                  </td>
                </tr>
              ) : (
                orders.map((order: any) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded border">
                        {order.orderNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 text-left">
                        {order.user?.name || order.user?.email || `User #${order.userId}`}
                      </div>
                      <div className="text-xs text-gray-500 text-left">{order.user?.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 text-left">
                        {order.subscription?.productName || order.product?.name || `Product #${order.productId}`}
                      </div>
                      <div className="text-xs text-gray-500 text-left">
                        {order.subscription?.productSlug || order.product?.slug}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                      <div>{order.periodStart} - {order.periodEnd}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 text-left">
                        {formatPrice(order.totalAmount)}
                      </div>
                      {order.discountAmount > 0 && (
                        <div className="text-xs text-gray-500 text-left">
                          Subtotal {formatPrice(order.subtotalAmount)} • Disc {formatPrice(order.discountAmount)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                      {formatDateTime(order.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openDetail(order)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="View Details"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                disabled={currentPage >= Math.ceil(total / pageSize)}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, total)}
                  </span>{' '}
                  of <span className="font-medium">{total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                    disabled={currentPage >= Math.ceil(total / pageSize)}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-gray-900">Order Detail</h2>
              <button
                onClick={closeDetail}
                className="text-gray-400 hover:text-gray-600"
              >
                <ChevronLeftIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Order Info</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-gray-500">Order Number</div>
                    <div className="font-mono text-gray-900">{selectedOrder.orderNumber}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Status</div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedOrder.status)}`}>
                        {selectedOrder.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Created At</div>
                    <div className="text-gray-900">{formatDateTime(selectedOrder.createdAt)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Paid At</div>
                    <div className="text-gray-900">{formatDateTime(selectedOrder.paidAt)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Period</div>
                    <div className="text-gray-900">
                      {selectedOrder.periodStart} - {selectedOrder.periodEnd}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">User & Product</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-gray-500">User</div>
                    <div className="text-gray-900">
                      {selectedOrder.user?.name || selectedOrder.user?.email || `User #${selectedOrder.userId}`}
                    </div>
                    {selectedOrder.user?.email && (
                      <div className="text-xs text-gray-500">{selectedOrder.user.email}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-gray-500">Product</div>
                    <div className="text-gray-900">
                      {selectedOrder.subscription?.productName || selectedOrder.product?.name || `Product #${selectedOrder.productId}`}
                    </div>
                    {(selectedOrder.subscription?.productSlug || selectedOrder.product?.slug) && (
                      <div className="text-xs text-gray-500">
                        {selectedOrder.subscription?.productSlug || selectedOrder.product?.slug}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Amounts</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-gray-500">Subtotal</div>
                    <div className="text-gray-900">{formatPrice(selectedOrder.subtotalAmount)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Discount</div>
                    <div className="text-gray-900">{formatPrice(selectedOrder.discountAmount)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Total</div>
                    <div className="text-gray-900 font-semibold">{formatPrice(selectedOrder.totalAmount)}</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Promo</h3>
                  {selectedOrder.promo ? (
                    <div>
                      <div className="text-gray-900">{selectedOrder.promo.code}</div>
                      <div className="text-xs text-gray-500">{selectedOrder.promo.name}</div>
                    </div>
                  ) : (
                    <div className="text-gray-500">No promo applied</div>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Affiliate</h3>
                  {selectedOrder.affiliateMember ? (
                    <div className="text-gray-900">{selectedOrder.affiliateMember.code}</div>
                  ) : (
                    <div className="text-gray-500">No affiliate</div>
                  )}
                </div>
              </div>

              {/* Payment Transaction Section */}
              {selectedOrder.paymentTransaction && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Payment Transaction</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-gray-500">Payment Status</div>
                        <div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(selectedOrder.paymentTransaction.status)}`}>
                            {selectedOrder.paymentTransaction.status?.toUpperCase() || 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Payment Provider</div>
                        <div className="text-gray-900">
                          {selectedOrder.paymentTransaction.provider?.name || selectedOrder.paymentTransaction.provider?.code || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Payment Channel</div>
                        <div className="text-gray-900">
                          {selectedOrder.paymentTransaction.paymentChannel?.displayName || selectedOrder.paymentTransaction.paymentChannel?.code || 'N/A'}
                        </div>
                        {selectedOrder.paymentTransaction.paymentChannel?.category && (
                          <div className="text-xs text-gray-500">{selectedOrder.paymentTransaction.paymentChannel.category}</div>
                        )}
                      </div>
                      <div>
                        <div className="text-gray-500">Payment Type</div>
                        <div className="text-gray-900">
                          {selectedOrder.paymentTransaction.providerPaymentType || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Amount</div>
                        <div className="text-gray-900 font-semibold">
                          {formatPrice(selectedOrder.paymentTransaction.amount)}
                          {selectedOrder.paymentTransaction.currency && ` ${selectedOrder.paymentTransaction.currency}`}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">VA Number</div>
                        <div className="text-gray-900 font-mono">
                          {selectedOrder.paymentTransaction.vaNumber || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Provider Order ID</div>
                        <div className="text-gray-900 font-mono text-xs">
                          {selectedOrder.paymentTransaction.providerOrderId || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Transaction ID</div>
                        <div className="text-gray-900 font-mono text-xs">
                          {selectedOrder.paymentTransaction.providerTransactionId || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Paid At</div>
                        <div className="text-gray-900">
                          {formatDateTime(selectedOrder.paymentTransaction.paidAt)}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Expired At</div>
                        <div className="text-gray-900">
                          {formatDateTime(selectedOrder.paymentTransaction.expiredAt)}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Created At</div>
                        <div className="text-gray-900">
                          {formatDateTime(selectedOrder.paymentTransaction.createdAt)}
                        </div>
                      </div>
                      {selectedOrder.paymentTransaction.redirectUrl && (
                        <div className="md:col-span-2">
                          <div className="text-gray-500">Payment URL</div>
                          <a
                            href={selectedOrder.paymentTransaction.redirectUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-xs break-all"
                          >
                            {selectedOrder.paymentTransaction.redirectUrl}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={closeDetail}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-imola-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionOrderManagement;


